import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const NewProjectSection = ({ onClose, BASE_URL, showCenteredPopup }) => {
    const [projectName, setProjectName] = useState("");
    const [description, setDescription] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [userStories, setUserStories] = useState([{ title: "", tasks: [{ title: "" }] }]);
    const [loading, setLoading] = useState(false);

    // Add a new user story
    const addUserStory = () => {
        setUserStories([...userStories, { title: "", tasks: [{ title: "" }] }]);
    };

    // Update user story title
    const updateUserStory = (index, value) => {
        const updated = [...userStories];
        updated[index].title = value;
        setUserStories(updated);
    };

    // Add new task inside a story
    const addTask = (storyIndex) => {
        const updated = [...userStories];
        updated[storyIndex].tasks.push({ title: "" });
        setUserStories(updated);
    };

    // Update a task title
    const updateTask = (storyIndex, taskIndex, value) => {
        const updated = [...userStories];
        updated[storyIndex].tasks[taskIndex].title = value;
        setUserStories(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!projectName || !description || !startDate) {
            alert("Please fill all fields before submitting.");
            return;
        }

        try {
            setLoading(true);
            const res = await fetch(`${BASE_URL}/admin/projects`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    project_name: projectName,
                    description,
                    start_date: startDate.toISOString().split("T")[0],
                    user_stories: userStories,
                }),
            });

            if (!res.ok) throw new Error("Failed to create project");
            showCenteredPopup("✅ Project and related stories added successfully!");
            setProjectName("");
            setDescription("");
            setStartDate(null);
            setUserStories([{ title: "", tasks: [{ title: "" }] }]);
            onClose();
        } catch (err) {
            console.error("Error creating project:", err);
            showCenteredPopup("❌ Failed to create project.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-gray-800 text-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 overflow-y-auto max-h-[90vh] animate-fadeIn">
                <h2 className="text-2xl font-bold mb-4 text-center">🧩 Add New Project</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Project Info */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Project Name</label>
                        <input
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter project name"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter short project description"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Start Date</label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Select project start date"
                            minDate={new Date()}
                            className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    {/* User Stories */}
                    <div>
                        <h3 className="text-lg font-semibold mb-2">📜 User Stories</h3>
                        {userStories.map((story, sIndex) => (
                            <div key={sIndex} className="bg-gray-700 p-4 rounded-md mb-3">
                                <input
                                    type="text"
                                    value={story.title}
                                    onChange={(e) => updateUserStory(sIndex, e.target.value)}
                                    placeholder="User Story Title"
                                    className="w-full bg-gray-600 border border-gray-500 rounded-md px-3 py-2 mb-3 text-white focus:ring-2 focus:ring-indigo-500"
                                />
                                <h4 className="text-sm font-medium mb-2">🧩 Tasks</h4>
                                {story.tasks.map((task, tIndex) => (
                                    <input
                                        key={tIndex}
                                        type="text"
                                        value={task.title}
                                        onChange={(e) => updateTask(sIndex, tIndex, e.target.value)}
                                        placeholder="Task title"
                                        className="w-full bg-gray-600 border border-gray-500 rounded-md px-3 py-2 mb-2 text-white focus:ring-2 focus:ring-indigo-500"
                                    />
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addTask(sIndex)}
                                    className="text-indigo-400 hover:text-indigo-300 text-sm"
                                >
                                    ➕ Add Task
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addUserStory}
                            className="mt-2 text-green-400 hover:text-green-300 text-sm"
                        >
                            ➕ Add Another User Story
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-md font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 rounded-md font-medium ${loading
                                    ? "bg-indigo-400 cursor-not-allowed"
                                    : "bg-indigo-600 hover:bg-indigo-700"
                                }`}
                        >
                            {loading ? "Saving..." : "Add Project"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewProjectSection;
